"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import {
  Newspaper,
  Home,
  Users,
  Network,
  ChevronDown,
  User,
  Settings,
  LogOut,
  BarChart3,
  FileText,
  Menu,
  X
} from "lucide-react";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

export default function Navbar() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we're on the client before accessing localStorage
        if (typeof window === 'undefined') return;

        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Only remove from localStorage if on client
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Function for smooth scrolling to a section
  const scrollToSection = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    
    // If we're not on the home page, navigate to home page with hash
    if (pathname !== '/') {
      router.push(`/#${sectionId}`);
      return;
    }

    // If we're on the home page, do smooth scroll
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Function to handle navigation to home
  const handleHomeClick = () => {
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      router.push('/');
    }
  };

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
    }
    setUser(null);
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    router.push('/');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close dropdown and mobile menu when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && 
          !mobileMenuRef.current.contains(event.target as Node) && 
          mobileMenuButtonRef.current && 
          !mobileMenuButtonRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
        }
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDropdownOpen, isMobileMenuOpen]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Focus on first menu item when opening
      const firstMenuItem = document.querySelector('[data-first-menu-item]');
      if (firstMenuItem instanceof HTMLElement) {
        setTimeout(() => firstMenuItem.focus(), 100);
      }
    } else {
      document.body.style.overflow = 'unset';
      // Return focus to menu button when closing
      if (mobileMenuButtonRef.current) {
        mobileMenuButtonRef.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Navigation items for smooth scrolling
  const navItems = [
    { id: "home", icon: Home, label: "Home" },
    { id: "news", icon: Newspaper, label: "News" },
    { id: "affiliates", icon: Network, label: "Affiliates" },
    { id: "about", icon: Users, label: "About" },
  ];

  if (loading) {
    return (
      <header 
        className="fixed top-0 left-0 w-full flex justify-between items-center px-4 md:px-8 py-4 z-50 bg-black"
        role="banner"
        aria-label="Main navigation"
      >
        <div className="flex items-center">
          <div className="w-32 h-10 bg-gray-700 animate-pulse rounded" aria-hidden="true" />
        </div>
        <div className="w-32 h-10 bg-gray-700 animate-pulse rounded hidden md:block" aria-hidden="true" />
        <div className="md:hidden w-10 h-10 bg-gray-700 animate-pulse rounded" aria-hidden="true" />
      </header>
    );
  }

  return (
    <header 
      className="fixed top-0 left-0 w-full flex justify-between items-center px-4 md:px-8 py-4 z-50 bg-black shadow-lg"
      role="banner"
      aria-label="Main navigation"
    >
      {/* Logo */}
      <button 
        onClick={handleHomeClick}
        className="flex items-center z-40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black rounded"
        aria-label="Go to home page"
      >
        <Image
          src="/mla.png"
          alt="NGO Logo"
          width={120}
          height={40}
          className="object-contain"
          priority
        />
      </button>

      {/* Desktop Menu */}
      <nav 
        className="hidden md:flex space-x-6 items-center"
        aria-label="Main navigation"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="hover:text-yellow-500 text-white flex items-center transition-colors duration-200 capitalize focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2 py-1"
              aria-label={`Go to ${item.label}`}
            >
              <Icon className="w-4 h-4 mr-1" aria-hidden="true" />
              {item.label}
            </button>
          );
        })}

        {user ? (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDropdown();
                }
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black"
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
              aria-label="User menu"
            >
              <span className="text-white max-w-32 truncate">Hello, {user.name.split(' ')[0]}</span>
              <ChevronDown
                className={`w-4 h-4 text-white transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            {isDropdownOpen && (
              <div 
                className="absolute right-0 mt-2 w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-lg py-2 z-40"
                role="menu"
                aria-orientation="vertical"
              >
                <div className="px-4 py-2 border-b border-gray-700">
                  <div className="text-sm font-medium text-white truncate">{user.name}</div>
                  <div className="text-xs text-gray-400 truncate">{user.email}</div>
                  {user.role === 'admin' && (
                    <div className="text-xs text-yellow-500 mt-1">Administrator</div>
                  )}
                </div>

                {user.role === 'admin' && (
                  <Link
                    href="/painel-adm"
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors focus:outline-none focus:bg-gray-800"
                    onClick={() => setIsDropdownOpen(false)}
                    role="menuitem"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" aria-hidden="true" />
                    Admin Panel
                  </Link>
                )}

                <Link
                  href="/profile"
                  className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors focus:outline-none focus:bg-gray-800"
                  onClick={() => setIsDropdownOpen(false)}
                  role="menuitem"
                >
                  <User className="w-4 h-4 mr-2" aria-hidden="true" />
                  My Profile
                </Link>
                <Link
                  href="/news"
                  className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors focus:outline-none focus:bg-gray-800"
                  onClick={() => setIsDropdownOpen(false)}
                  role="menuitem"
                >
                  <FileText className="w-4 h-4 mr-2" aria-hidden="true" />
                  My News
                </Link>

                <Link
                  href="/settings"
                  className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors focus:outline-none focus:bg-gray-800"
                  onClick={() => setIsDropdownOpen(false)}
                  role="menuitem"
                >
                  <Settings className="w-4 h-4 mr-2" aria-hidden="true" />
                  Settings
                </Link>

                <div className="border-t border-gray-700 my-1" aria-hidden="true" />

                <button
                  onClick={handleLogout}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors focus:outline-none focus:bg-red-500/10"
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                  Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link
            href="/login"
            className="px-5 py-2 bg-yellow-500 text-black rounded-full font-semibold hover:opacity-90 transition flex items-center focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black"
            aria-label="Login"
          >
            <User className="w-4 h-4 mr-2" aria-hidden="true" />
            Login
          </Link>
        )}
      </nav>

      {/* Mobile Menu Button */}
      <button
        ref={mobileMenuButtonRef}
        className="md:hidden mobile-menu-button z-40 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black"
        onClick={toggleMobileMenu}
        aria-expanded={isMobileMenuOpen}
        aria-controls="mobile-menu"
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
      >
        {isMobileMenuOpen ? (
          <X className="w-6 h-6 text-white" aria-hidden="true" />
        ) : (
          <Menu className="w-6 h-6 text-white" aria-hidden="true" />
        )}
      </button>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        ref={mobileMenuRef}
        className={`md:hidden fixed inset-0 top-0 left-0 w-full h-screen bg-gray-900 z-40 pt-20 px-6 transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isMobileMenuOpen}
      >
        {/* Background overlay */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 -z-10"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />

        {/* Menu Content */}
        <div className="relative bg-gray-900 rounded-lg p-6 max-h-[calc(100vh-5rem)] overflow-y-auto">
          {/* Mobile Navigation Items */}
          <nav className="flex flex-col space-y-4" aria-label="Mobile navigation">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  data-first-menu-item={index === 0 ? true : undefined}
                  className="text-white text-lg flex items-center py-3 px-4 border-b border-gray-700 hover:text-yellow-500 hover:bg-gray-800 transition-colors capitalize text-left w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:bg-gray-800"
                  aria-label={`Go to ${item.label}`}
                >
                  <Icon className="w-5 h-5 mr-3" aria-hidden="true" />
                  {item.label}
                </button>
              );
            })}

            {/* Mobile User Area */}
            {user ? (
              <div className="border-t border-gray-700 pt-6 mt-4">
                {/* User Information */}
                <div className="px-4 py-3 mb-4 bg-gray-800 rounded-lg">
                  <div className="text-base font-medium text-white truncate">{user.name}</div>
                  <div className="text-sm text-gray-400 truncate">{user.email}</div>
                  {user.role === 'admin' && (
                    <div className="text-xs text-yellow-500 mt-1">Administrator</div>
                  )}
                </div>

                {/* User Links */}
                <nav className="space-y-2" aria-label="User menu">
                  {user.role === 'admin' && (
                    <Link
                      href="/admin-panel"
                      className="flex items-center px-4 py-3 text-white hover:bg-gray-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:bg-gray-800"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <BarChart3 className="w-5 h-5 mr-3" aria-hidden="true" />
                      Admin Panel
                    </Link>
                  )}

                  <Link
                    href="/profile"
                    className="flex items-center px-4 py-3 text-white hover:bg-gray-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:bg-gray-800"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <User className="w-5 h-5 mr-3" aria-hidden="true" />
                    My Profile
                  </Link>

                  <Link
                    href="/my-news"
                    className="flex items-center px-4 py-3 text-white hover:bg-gray-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:bg-gray-800"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FileText className="w-5 h-5 mr-3" aria-hidden="true" />
                    My News
                  </Link>

                  <Link
                    href="/settings"
                    className="flex items-center px-4 py-3 text-white hover:bg-gray-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:bg-gray-800"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Settings className="w-5 h-5 mr-3" aria-hidden="true" />
                    Settings
                  </Link>

                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full text-left px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors mt-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-red-500/10"
                  >
                    <LogOut className="w-5 h-5 mr-3" aria-hidden="true" />
                    Logout
                  </button>
                </nav>
              </div>
            ) : (
              <div className="border-t border-gray-700 pt-6 mt-4">
                <Link
                  href="/login"
                  className="flex items-center justify-center px-4 py-3 bg-yellow-500 text-black rounded-full font-semibold hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="w-5 h-5 mr-2" aria-hidden="true" />
                  Login
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Overlay to close menu when clicking outside (mobile only) */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
}